import {
  useIsMutating,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core";
import { QueryTags } from "../consts";
import { GaripalliDirectusClient } from "../directus/client";
import { GaripalliIDB, SyncMetadata } from "../extensions/idb";
import { STATS_BROADCAST_CHANNEL_NAME } from "../extensions/sw/modules/stats/module";
import { maxItem } from "../libs/utils";
import {
  ServiceWorkerAppActions,
  ServiceWorkerAppStateKeys,
} from "../libs/workers/consts";
import { useDirectus } from "../providers/DirectusProvider";
import { useIndexedDb } from "../providers/IndexDbProvider";
import { usePWA } from "../providers/PWAProvider";
import { syncEvents } from "../services/events";
import { syncMatches } from "../services/matches";

const QueryKeys = {
  SYNC_METADATA: ["syncMetadata"] as const,
} as const;

const MutationKeys = {
  DIRECT_SYNC: ["directSync"] as const,
  BACKGROUND_SYNC: ["backgroundSync"] as const,
} as const;

/**
 * Hook per recuperare i metadati di sincronizzazione da IndexedDB
 */
export const useSyncMetadata = () => {
  const { getDb } = useIndexedDb();

  return useQuery({
    queryKey: QueryKeys.SYNC_METADATA,
    queryFn: async (): Promise<SyncMetadata | null> => {
      console.log("🔍 Fetching sync metadata from IndexedDB...");
      const db = await getDb();
      try {
        const metadata = await db.get("sync_metadata", "lastSync");
        return metadata || null;
      } catch (error) {
        console.error("Error fetching sync metadata:", error);
        return null;
      } finally {
        db.close();
      }
    },
    refetchInterval: 10000, // Refetch every 10 seconds
  });
};

// ✅ Sincronizzazione diretta (no Service Worker)
const syncDirectly = async (
  client: GaripalliDirectusClient,
  db: GaripalliIDB
): Promise<boolean> => {
  try {
    console.log(`🔄 Starting direct sync...`);

    const [matchesResult, eventsResult] = await Promise.allSettled([
      syncMatches(client, db),
      syncEvents(client, db),
    ]);

    const matches =
      matchesResult.status === "fulfilled" ? matchesResult.value : [];
    const events =
      eventsResult.status === "fulfilled" ? eventsResult.value : [];

    if (matchesResult.status === "rejected") {
      console.error("❌ Matches sync failed:", matchesResult.reason);
    }
    if (eventsResult.status === "rejected") {
      console.error("❌ Events sync failed:", eventsResult.reason);
    }

    console.log(
      `📥 Fetched ${matches.length} matches and ${events.length} events`
    );

    console.log("✅ Direct sync completed successfully");
    return Promise.resolve(true);
  } catch (error) {
    return Promise.reject(error);
  }
};

const syncViaServiceWorker = (accessToken: string): Promise<boolean> => {
  const abortController = new AbortController();
  return new Promise<boolean>(async (resolve, reject) => {
    try {
      const registration = await navigator.serviceWorker.getRegistration();

      if (!navigator.serviceWorker || !registration?.active) {
        throw new Error("No active Service Worker found");
      }
      registration.active.addEventListener(
        "error",
        (event) => {
          console.error("❌ SW sync error:", event);
          reject(event);
        },
        { signal: abortController.signal }
      );

      registration.active.addEventListener(
        "messageerror",
        () => {
          console.error("❌ SW sync message error");
          reject("SW message error");
        },
        { signal: abortController.signal }
      );

      const broadcastChannel = new BroadcastChannel(
        STATS_BROADCAST_CHANNEL_NAME
      );

      broadcastChannel.addEventListener(
        "message",
        (event: any) => {
          console.log("[🔄 Sync ] SW message received:", event);
          const { type, data }: { type: string; data: any } = event.data ?? {};
          if (type === ServiceWorkerAppStateKeys.SYNC_COMPLETE) {
            console.log(
              `✅ SW sync completed: ${data.matches} matches and ${data.events} events synced.`
            );
            resolve(true);
          } else if (type === ServiceWorkerAppStateKeys.SYNC_ERROR) {
            console.error("❌ SW sync failed:", data.error);
            reject(data.error);
          }
        },
        { signal: abortController.signal }
      );

      registration.active.postMessage({
        type: ServiceWorkerAppActions.LAUNCH_BACKGROUND_SYNC,
        auth: { accessToken },
        data: { tag: "initial-data-sync" },
      });

      console.log("🔄 Background sync registered");
    } catch (error) {
      console.error("❌ Failed to start SW sync:", error);
      reject(error);
    }
  }).finally(() => {
    console.log("🛑 Cleaning up SW sync listeners");
    abortController.abort();
  });
};

export function useBlockingDataSyncThread() {
  const { client: directusClient } = useDirectus();
  const { getDb } = useIndexedDb();
  return useMutation({
    mutationKey: MutationKeys.DIRECT_SYNC,
    mutationFn: async () => {
      if (!directusClient) {
        return Promise.reject("No Directus client available");
      } else {
        const db = await getDb();
        try {
          await syncDirectly(directusClient, db);
          return Promise.resolve(true);
        } finally {
          db.close();
        }
      }
    },
  });
}

export function useBlockingDataSyncStatus() {
  return useMutationState({
    filters: { mutationKey: MutationKeys.DIRECT_SYNC },
    select: (mutation) => mutation.state.status,
  });
}

export function useBackgroundSync(
  { notifyOnSuccess = false } = { notifyOnSuccess: false }
) {
  const { accessToken } = useDirectus();
  const pwa = usePWA();
  const queryClient = useQueryClient();
  const dialogs = useDialogs();

  return useMutation({
    mutationKey: MutationKeys.BACKGROUND_SYNC,
    mutationFn: async () => {
      if (!accessToken) {
        return Promise.reject("No access token available");
      } else if (pwa.status.isPWASupported) {
        return syncViaServiceWorker(accessToken);
      } else {
        return Promise.reject("No PWA support for background sync");
      }
    },
    onSuccess(data, variables, context) {
      console.log("✅ Background sync mutation succeeded:", data);
      if (notifyOnSuccess) {
        dialogs.confirm("Sincronizzazione completata con successo!", {
          okText: "Aggiorna dati",
          cancelText: "Mantieni vista corrente",
          onClose: async (result) => {
            if (result) {
              queryClient.resetQueries({
                queryKey: [QueryTags.SERVICE_WORKER_BASED],
              });
            } else {
              queryClient.invalidateQueries({
                queryKey: [QueryTags.SERVICE_WORKER_BASED],
              });
            }
          },
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: [QueryTags.SERVICE_WORKER_BASED],
        });
      }

      queryClient.invalidateQueries({
        queryKey: QueryKeys.SYNC_METADATA,
      });
    },
  });
}

export function useBackgroundSyncsState() {
  const result = useMutationState({
    filters: { mutationKey: MutationKeys.BACKGROUND_SYNC },
  });

  if (result.length > 1) {
    console.warn(
      "Multiple background sync mutations found, returning the first one"
    );
  }

  return result[0];
}

export function useBackgroundSyncsRunningMutationsCount() {
  return useIsMutating({
    mutationKey: MutationKeys.BACKGROUND_SYNC,
  });
}

export function useDirectSyncsRunningMutationsCount() {
  return useIsMutating({
    mutationKey: MutationKeys.DIRECT_SYNC,
  });
}

export function useSyncsRunningMutationsCount() {
  const directSyncs = useDirectSyncsRunningMutationsCount();
  const backgroundSyncs = useBackgroundSyncsRunningMutationsCount();
  return directSyncs + backgroundSyncs;
}

export function useLatestBackgroundSyncMutation() {
  const mostRecentRuns = useMutationState({
    filters: { mutationKey: MutationKeys.BACKGROUND_SYNC },
  }).sort((a, b) => b.submittedAt - a.submittedAt);

  const lastRun = mostRecentRuns[0];

  return lastRun;
}

export function useLatestDirectSyncMutation() {
  const mostRecentRuns = useMutationState({
    filters: { mutationKey: MutationKeys.DIRECT_SYNC },
  }).sort((a, b) => b.submittedAt - a.submittedAt);

  const lastRun = mostRecentRuns[0];

  return lastRun;
}

export function useLatestSyncMutation() {
  const backgroundSync = useLatestBackgroundSyncMutation();
  const directSync = useLatestDirectSyncMutation();

  return maxItem([backgroundSync, directSync], (m) => m?.submittedAt ?? 0);
}
